import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiRequest } from '../services/apiClient';

/**
 * Loads the employee profile bundle for either a manager-backed deep link
 * or a direct employee-profile selection from the Workforce directory.
 * Returns:
 *   { bundle, status, isLoading, error, refetch }
 *
 *   status === 'no-profile' — server returned 404 (manager has no
 *     profile row yet). UI surfaces the "no profile yet" empty state.
 *   status === 'loaded'     — bundle present.
 *   status === 'idle'       — selection is falsy / hook is disabled.
 *   status === 'error'      — non-404 server / network error.
 *   status === 'loading'    — initial fetch in flight.
 */
export function useEmployeeProfile(selection) {
  const managerId = Number(selection?.managerId);
  const profileId = Number(selection?.profileId);
  const hasManagerId = Number.isFinite(managerId) && managerId > 0;
  const hasProfileId = Number.isFinite(profileId) && profileId > 0;
  const requestPath = hasProfileId
    ? `/api/employees/profiles/${profileId}`
    : (hasManagerId ? `/api/employees/profiles/by-manager/${managerId}` : null);
  const [bundle, setBundle] = useState(null);
  const [status, setStatus] = useState(requestPath ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const fetch = useCallback(async () => {
    if (!requestPath) {
      setStatus('idle');
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('loading');
    setError('');

    try {
      const data = await apiRequest(requestPath, {
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setBundle(data);
      setStatus('loaded');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError && err.status === 404) {
        setBundle(null);
        setStatus('no-profile');
        return;
      }
      setError(err?.message || 'Unable to load employee profile.');
      setStatus('error');
    }
  }, [requestPath]);

  useEffect(() => {
    fetch();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetch]);

  return {
    bundle,
    status,
    isLoading: status === 'loading',
    error,
    refetch: fetch
  };
}
